import type { Location } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import type { OrderListSearchConfig } from 'src/distribution_platforms/features/order_list';
import type { OrderStatus, SpecimenType } from 'src/types';
import type { OrderStatusFilterOption } from 'src/types/status_filter';

/**
 * Fields needed for list row navigation and edit guards.
 * Satisfied by both list-item and detail order fragments.
 */
export type OrderListRowLinkFields = {
  id: string;
  status: OrderStatus;
  createdByCs?: boolean;
  creatorAuth0Id?: string | null;
};

/**
 * Base workflow definition shared across all platforms.
 * Platform-specific interfaces extend this.
 */
export interface WorkflowDefinition {
  /** Whether this workflow allows creating an order in the portal. */
  canCreateOrder: boolean;

  /** Whether the user can edit an order. */
  canEditOrder(order: OrderListRowLinkFields, userInfoId: string): boolean;

  /** Returns the link for the order row. */
  getRowLink(
    order: OrderListRowLinkFields,
    organization: string,
    location: Location,
    userId: string,
  ): {
    pathname: string;
    state?: { background: { pathname: string; search: string } };
  };

  /** Placeholder and width for the order list search input. */
  orderListSearchConfig: OrderListSearchConfig;

  /**
   * Returns the status filter options for the order list, keyed by filter value.
   * Used by the order list page to build the status dropdown and to resolve URL params to query statuses.
   */
  getStatusFilterOptions(): Map<string, OrderStatusFilterOption>;

  /** Defines the supported specimen types for this workflow. */
  supportedSpecimenTypes: Set<SpecimenType>;

  /** Returns the display name for an order status. */
  getStatusDisplayName(status: OrderStatus): string;

  /** Returns the column definitions for the order list table. */
  getOrderListColumns(workflow: WorkflowDefinition): OrderListColumnDef[];
}

/**
 * Column defs for the order list table.
 *
 * Row shape differs by platform (LDT list item vs SaMD list/detail fields).
 * At the shared workflow boundary columns are opaque to consumers (DataTable),
 * so the row type is `object`. Cell value type is `unknown` (not `any`).
 */
export type OrderListColumnDef = ColumnDef<object, unknown>;

/**
 * Widens platform-typed column defs to the shared workflow return type.
 *
 * TanStack's `ColumnDef` is invariant in `TValue`, so a heterogeneous accessor
 * array cannot be typed as `ColumnDef<TData, unknown>[]`. Column construction
 * stays fully typed via `createColumnHelper`; this is only the list/workflow
 * boundary. Safe because list pages render cells only through these defs.
 */
export function toOrderListColumns(
  columns: ReadonlyArray<object>,
): OrderListColumnDef[] {
  return columns as OrderListColumnDef[];
}
